import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Grid, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import {
    MERCHANT_ADMIN_PRODUCTS_GROUP_CREATE_ROUTE,
    MERCHANT_ADMIN_PRICE_LISTS_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../../constants/routes"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardTabs } from "../../../../../shared/components/tabs/StandardTabs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { AllProductGroupsTab } from "./AllProductGroupsTab"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchPriceListsResponse, PriceListQuery, fetchPriceLists } from "../../priceList/priceListApi"
import { useMerchant } from "../../../../merchants/merchantHooks"

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists || []

const useStyles = makeStyles()(() => ({
    emptyStateText: {
        color: "#64748B",
        paddingBottom: ".5rem",
    },
}))

export function ProductGroupPage() {
    const navigate = useNavigate()
    const { merchantId } = useMerchant()
    const { classes } = useStyles()

    const { data: priceLists = [], isLoading } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectPriceLists,
    })

    const hasPriceLists = priceLists.length > 0

    const tabs = useMemo(
        () =>
            priceLists.map((priceList) => ({
                label: priceList.name,
                component: <AllProductGroupsTab priceListId={priceList.id} />,
            })),
        [priceLists],
    )

    return (
        <>
            <LocalizedStrict id="product-groups-page-header-title" attrs={{ title: true }}>
                <PageHeader
                    title="Product groups"
                    page={Page.MERCHANT_PRODUCT_GROUP}
                    rightElement={
                        hasPriceLists ? (
                            <LocalizedStrict id="product-groups-create-button">
                                <StandardButton onClick={() => navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_CREATE_ROUTE)}>
                                    Create new
                                </StandardButton>
                            </LocalizedStrict>
                        ) : undefined
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-groups">
                            <Breadcrumb>Product groups</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            {!isLoading && hasPriceLists && <StandardTabs tabs={tabs} />}

            {!isLoading && !hasPriceLists && (
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                    gap={3}
                    marginTop="10rem"
                >
                    <LocalizedStrict id="product-groups-no-price-lists-message">
                        <Typography variant="h6" className={classes.emptyStateText}>
                            You don't have a price list yet. You must create a price list before creating product
                            groups.
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="products-go-to-price-lists-button">
                        <StandardButton
                            onClick={() => {
                                navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE)
                            }}
                        >
                            Go to price lists
                        </StandardButton>
                    </LocalizedStrict>
                </Grid>
            )}
        </>
    )
}
